import type { WovarProduct } from "@commercekit/api";
import { useCallback } from "react";

declare global {
  interface Window {
    klaviyo: {
      track: (eventName: string, properties: any) => void;
      trackViewedItem: (properties: any) => void;
      identify: (properties: any) => void;
      isIdentified: () => Promise<boolean>;
    };
  }
}

/**
 * Hook for tracking Klaviyo "Viewed Product" events
 * Based on Klaviyo's documentation for viewed product tracking
 * @see https://developers.klaviyo.com/en/docs/guide_to_integrating_a_platform_without_a_pre_built_klaviyo_integration#viewed-product-tracking-snippet
 */
export const useKlaviyoViewedProduct = () => {
  return useCallback((product: WovarProduct) => {
    if (typeof window !== "undefined" && window.klaviyo && product) {
      try {
        // Prepare product categories array
        const categories =
          product.categories?.map((cat) => cat.name).filter(Boolean) || [];

        // Get the primary category (last in hierarchy for breadcrumbs)
        const primaryCategory = product.categories?.length
          ? product.categories[product.categories.length - 1]?.name
          : "";

        // Prepare the Viewed Product event data according to Klaviyo specs
        const viewedProductData = {
          ProductName: product.name,
          ProductID: product.id,
          SKU: product.sku,
          Categories: categories,
          ImageURL:
            product.defaultImage?.urlOriginal ||
            product.images?.[0]?.urlOriginal ||
            "",
          URL: typeof window !== "undefined" ? window.location.href : "",
          Brand: "Wovar", // Based on the schema.org meta tag in WvProductPage
          Price:
            product.salePriceInclTax?.value || product.priceInclTax?.value || 0,
          CompareAtPrice: product.salePriceInclTax
            ? product.priceInclTax?.value
            : undefined,
        };

        // Track the Viewed Product event
        window.klaviyo.track("Viewed Product", viewedProductData);

        // Also track for "Recently Viewed Items" feature
        window.klaviyo.trackViewedItem({
          Title: product.name,
          ItemId: product.id,
          Categories: categories,
          ImageUrl:
            product.defaultImage?.urlOriginal ||
            product.images?.[0]?.urlOriginal ||
            "",
          Url: typeof window !== "undefined" ? window.location.href : "",
          Metadata: {
            Brand: "Wovar",
            Price:
              product.salePriceInclTax?.value ||
              product.priceInclTax?.value ||
              0,
            CompareAtPrice: product.salePriceInclTax
              ? product.priceInclTax?.value
              : undefined,
            SKU: product.sku,
            ProductID: product.id,
            PrimaryCategory: primaryCategory,
            StockLevel: product.stockLevel,
            Description: product.description,
          },
        });

        // console.log("Klaviyo Viewed Product tracked:", {
        //   productName: product.name,
        //   sku: product.sku,
        //   categories: categories,
        // });
      } catch (error) {
        console.error("Error tracking Klaviyo Viewed Product:", error);
      }
    }
  }, []);
};

/**
 * Hook for identifying users in Klaviyo
 */
export const useKlaviyoIdentify = () => {
  return useCallback(
    (email?: string, additionalProperties?: Record<string, any>) => {
      if (typeof window !== "undefined" && window.klaviyo && email) {
        try {
          const identifyData = {
            email,
            ...additionalProperties,
          };

          window.klaviyo.identify(identifyData);
          // console.log("Klaviyo user identified:", email);
        } catch (error) {
          console.error("Error identifying user in Klaviyo:", error);
        }
      }
    },
    []
  );
};

/**
 * Generic hook for tracking custom Klaviyo events
 */
export const useKlaviyoTrackEvent = () => {
  return useCallback((eventName: string, properties: Record<string, any>) => {
    if (typeof window !== "undefined" && window.klaviyo) {
      try {
        window.klaviyo.track(eventName, properties);
        console.log(`Klaviyo event tracked: ${eventName}`, properties);
      } catch (error) {
        console.error(`Error tracking Klaviyo event ${eventName}:`, error);
      }
    }
  }, []);
};
